import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCreditCard,
  faGem,
  faHistory,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import paymentService, {
  CreditTransaction,
  PayUFormData,
} from '../../services/paymentService';
import { getApiErrorMessage } from '../../api/axiosInstance';
import CreditsDialog from './CreditsDialog';
import CreditPackGrid, { CreditPackOption } from './CreditPackGrid';
import { PayURedirectRequest } from './PayURedirectForm';
import {
  WalletDialogProps,
  PendingCheckout,
  clearPendingCheckout,
  readPendingCheckout,
} from './walletProviders';
import './ServiceWallet.css';

const toPayURequest = (data: PayUFormData): PayURedirectRequest => ({
  action: `${data.payu_base_url}/_payment`,
  values: {
    key: data.key,
    txnid: data.txnid,
    amount: data.amount,
    productinfo: data.productinfo,
    firstname: data.firstname,
    email: data.email,
    phone: data.phone,
    surl: data.surl,
    furl: data.furl,
    hash: data.hash,
    service_provider: data.service_provider,
  },
});

type CheckState = 'success' | 'failed' | 'pending' | 'unknown' | null;

const InfiniCareCreditsDialog: React.FC<WalletDialogProps> = ({
  open,
  onClose,
  onCheckout,
  returnPath,
  balance,
  onSettled,
}) => {
  const { t } = useTranslation('services');
  const [packs, setPacks] = useState<CreditPackOption[]>([]);
  const [history, setHistory] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<PendingCheckout | null>(null);
  const [checking, setChecking] = useState(false);
  const [checkState, setCheckState] = useState<CheckState>(null);
  const [reportCost, setReportCost] = useState(1);

  const checkStatus = useCallback(
    async (record: PendingCheckout) => {
      setChecking(true);
      setCheckState(null);
      try {
        const result = await paymentService.verifyTransaction(record.txnId);
        if (result.status === 'success') {
          setCheckState('success');
          clearPendingCheckout();
          setPending(null);
          onSettled();
        } else if (result.status === 'pending') {
          setCheckState('pending');
        } else {
          setCheckState(result.status === 'refunded' ? 'success' : 'failed');
          clearPendingCheckout();
          setPending(null);
          if (result.status === 'refunded') onSettled();
        }
      } catch {
        setCheckState('unknown');
      } finally {
        setChecking(false);
      }
    },
    [onSettled],
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setError('');
    setLoading(true);
    paymentService
      .getCreditPacks()
      .then((data) => {
        if (cancelled) return;
        setPacks(
          data.map((pack) => ({
            id: pack.id,
            name: pack.name,
            credits: pack.credits,
            bonusCredits: pack.bonus_credits,
            totalCredits: pack.total_credits,
            price: parseFloat(pack.price),
            currency: pack.currency,
            description: pack.description,
            features: pack.features,
            featured: pack.total_credits >= 10,
            badge: pack.total_credits >= 10 ? t('wallet.bestValue') : undefined,
          })),
        );
      })
      .catch((err) => {
        if (!cancelled) {
          setPacks([]);
          setError(getApiErrorMessage(err, t('wallet.loadPacksFailed')));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    setHistoryLoading(true);
    paymentService
      .getCreditHistory()
      .then((rows) => {
        if (!cancelled) setHistory(rows.slice(0, 5));
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });

    const stored = readPendingCheckout();
    setPending(stored);
    setCheckState(null);
    if (stored) void checkStatus(stored);

    paymentService
      .getCreditCosts()
      .then((costs) => {
        if (!cancelled && costs.health_insights) setReportCost(costs.health_insights);
      })
      .catch(() => {
        // Fall back to the default report cost.
      });

    return () => {
      cancelled = true;
    };
  }, [open, checkStatus, t]);

  const handleBuy = async (packId: number) => {
    setProcessingId(packId);
    setError('');
    const pack = packs.find((item) => item.id === packId);
    try {
      const response = await paymentService.buyCredits(packId, returnPath);
      onCheckout(toPayURequest(response.payu_data), {
        txnId: response.payu_data.txnid,
        packName: pack ? pack.name : '',
      });
    } catch (err) {
      setError(getApiErrorMessage(err, t('wallet.startPaymentFailed')));
      setProcessingId(null);
    }
  };

  const historyLabel = (row: CreditTransaction) => {
    switch (row.transaction_type) {
      case 'purchase':
        return t('wallet.history.purchase');
      case 'bonus':
        return t('wallet.history.bonus');
      case 'usage':
        return t('wallet.history.usage');
      case 'refund':
        return t('wallet.history.refund');
      default:
        return t('wallet.history.adjustment');
    }
  };

  return (
    <CreditsDialog
      open={open}
      onClose={onClose}
      title={t('wallet.title')}
      subtitle={t('wallet.subtitle')}
      icon={faCreditCard}
    >
      <div className="sw-balance-row">
        <FontAwesomeIcon icon={faGem} />
        <span className="sw-balance-label">{t('wallet.currentBalance')}</span>
        <span className="sw-balance-value">
          {balance === null ? '\u2014' : balance}
        </span>
      </div>

      <ul className="sw-usage-list">
        <li className="sw-usage-item">
          <span className="sw-usage-dot sw-usage-dot--active" />
          {t('wallet.usageHealthInsights', { cost: reportCost })}
        </li>
        <li className="sw-usage-item sw-usage-item--muted">
          <span className="sw-usage-dot" />
          {t('wallet.usageSmartReport')}
        </li>
      </ul>

      {pending && (
        <div className={`sw-checkout-state sw-checkout-state--${checkState ?? 'pending'}`}>
          <FontAwesomeIcon
            icon={checking ? faSpinner : checkState === 'success' ? faGem : faTriangleExclamation}
            spin={checking}
          />
          <div className="sw-checkout-copy">
            <strong>{pending.packName || pending.txnId}</strong>
            <span>
              {checking
                ? t('wallet.checking')
                : checkState === 'success'
                ? t('wallet.checkoutSuccess', { balance: balance ?? 0 })
                : checkState === 'failed'
                ? t('wallet.checkoutFailed')
                : checkState === 'unknown'
                ? t('wallet.checkoutUnknown')
                : t('wallet.checkoutPending')}
            </span>
          </div>
          <button
            type="button"
            className="sw-checkout-action"
            onClick={() => checkStatus(pending)}
            disabled={checking}
          >
            {t('wallet.checkStatus')}
          </button>
        </div>
      )}

      {error && (
        <div className="sw-dialog-error">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="sw-dialog-status">
          <FontAwesomeIcon icon={faSpinner} spin /> {t('wallet.loadingPacks')}
        </div>
      ) : packs.length === 0 ? (
        <div className="sw-dialog-status">{t('wallet.noPacks')}</div>
      ) : (
        <CreditPackGrid packs={packs} onBuy={handleBuy} processingId={processingId} />
      )}

      <section className="sw-history">
        <header className="sw-history-head">
          <h4>
            <FontAwesomeIcon icon={faHistory} /> {t('wallet.recentActivity')}
          </h4>
          <Link to="/my-transactions" className="sw-history-link" onClick={onClose}>
            {t('wallet.viewAllHistory')}
          </Link>
        </header>
        {historyLoading ? (
          <div className="sw-dialog-status">
            <FontAwesomeIcon icon={faSpinner} spin /> {t('wallet.loadingHistory')}
          </div>
        ) : history.length === 0 ? (
          <p className="sw-history-empty">{t('wallet.noHistory')}</p>
        ) : (
          <ul className="sw-history-list">
            {history.map((row) => (
              <li key={row.id} className="sw-history-row">
                <div className="sw-history-main">
                  <strong>{historyLabel(row)}</strong>
                  {row.description && <span>{row.description}</span>}
                  <span className="sw-history-date">
                    {new Date(row.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="sw-history-amounts">
                  <span className={row.amount >= 0 ? 'sw-amount-plus' : 'sw-amount-minus'}>
                    {row.amount >= 0 ? '+' : ''}
                    {row.amount}
                  </span>
                  <span className="sw-history-balance">
                    {t('wallet.balanceAfter', { balance: row.balance_after })}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </CreditsDialog>
  );
};

export default InfiniCareCreditsDialog;
